import React from 'react';
import {
  BORDER, TAN, DocTitle, SectionBar, Panel, Field, SelectField, InitialsField, SignatureField,
  US_STATES, REQ, V,
} from './ui';

/** Page 1 of the Word document – "SUBSCRIPTION AGREEMENT" */
const StepCompanyInfo = ({ register, errors, watch }) => {
  const legalName = watch('companyInfo.legalName');
  const f = { register, errors };

  return (
    <div>
      <DocTitle>Subscription Agreement</DocTitle>

      {/* ---------- company block ---------- */}
      <Panel className="grid grid-cols-1 gap-x-8 gap-y-4 p-4 md:grid-cols-12 md:p-5">
        <Field {...f} name="companyInfo.legalName" label="Legal Business Name:" className="md:col-span-8" rules={{ required: REQ }} />
        <Field {...f} name="companyInfo.ein" label="EIN:" mask="ein" placeholder="XX-XXXXXXX" className="md:col-span-4" rules={{ required: REQ, validate: V.ein }} />
        <Field {...f} name="companyInfo.dbaName" label="Trade Name (if applicable):" className="md:col-span-12" />
        <Field {...f} name="companyInfo.address" label="Business Address:" className="md:col-span-12" rules={{ required: REQ }} />
        <Field {...f} name="companyInfo.city" label="City:" className="md:col-span-5" rules={{ required: REQ }} />
        <SelectField {...f} name="companyInfo.state" label="State:" options={US_STATES.map(([a]) => [a, a])} className="md:col-span-3" rules={{ required: REQ }} />
        <Field {...f} name="companyInfo.zip" label="Zip Code:" mask="zip" placeholder="12345" className="md:col-span-4" rules={{ required: REQ, validate: V.zip }} />
        <Field {...f} name="companyInfo.phone" label="Business Phone:" mask="phone" placeholder="(555) 555-5555" className="md:col-span-6" rules={{ required: REQ, validate: V.phone }} />
      </Panel>

      <p className="mt-6 text-[13px] leading-relaxed text-gray-700">
        I (we) hereby authorize <strong>Everee, Inc.</strong>, or its agent or assignee, hereinafter called “Company” to utilize the
        following billing method(s) for the monthly Everee subscription:
      </p>

      {/* ---------- fees ---------- */}
      <div className="mt-4">
        <SectionBar>Fees</SectionBar>
        <div className={`border border-t-0 ${BORDER} py-3 text-center text-sm font-bold text-gray-800`}>
          *SEE SAAS PROVIDER AGREEMENT FOR PRICING DETAILS
        </div>
      </div>

      {/* ---------- terms ---------- */}
      <div className={`mt-6 border ${BORDER}`}>
        <div className={`grid grid-cols-[84px_1fr] border-b ${BORDER} ${TAN} text-xs font-bold tracking-[0.2em] text-gray-700`}>
          <div className={`border-r ${BORDER} px-2 py-2 text-center`}>INITIAL</div>
          <div className="px-4 py-2 text-center">TERMS</div>
        </div>
        <div className="grid grid-cols-[84px_1fr]">
          <div className={`flex justify-center border-r ${BORDER} bg-[#faf9f7] px-1 py-4`}>
            <InitialsField name="agreement.subscription.initials" {...f} />
          </div>
          <div className="space-y-3 p-4 text-[13px] leading-relaxed text-gray-700 md:p-5">
            <p>
              <strong>I (we)</strong> have read and agree to the Terms and Conditions, Privacy Policy, Payroll Service Terms and
              Cardholder Agreement found at the bottom of{' '}
              <a href="https://www.everee.com/" target="_blank" rel="noopener noreferrer" className="text-blue-700 underline">
                https://www.everee.com/
              </a>.
            </p>
            <p>
              Everee generally requires traditional ACH processing (3-day settlement) for the first 30-days of service (Initial
              Period) and after the Initial Period a transition to same-day ACH processing may occur. Everee offers this transition
              as a service provided certain repayment and credit requirements are met and maintained. Everee reserves the right to
              transition to same-day ACH processing upon connection of your business bank account to a third-party integrator,
              receiving additional bank statements, other evidential matters that our team may request, or obtaining a pre-funded
              cash balance. Everee reserves the right to transition service back to traditional ACH processing immediately upon
              providing notice to you. Same-day ACH processing may occur from day 1 of service upon review and approval from our
              credit and onboarding team. To expedite this review, please provide your last 3 months of bank statements.
            </p>
            <p>
              <strong>I (we)</strong> agree to pay an ACH Funding Failure fee in the event any Automated Clearing House (“ACH”)
              transaction is rejected or returned for any reason, including but not limited to insufficient funds, a frozen or
              closed account, or revoked authorization. Such fee shall be assessed per failed transaction and included on the next
              monthly invoice.
            </p>
          </div>
        </div>
      </div>

      {/* ---------- signatures ---------- */}
      <div className="mt-8 grid grid-cols-1 gap-8 md:grid-cols-2">
        <div className="space-y-4">
          <p className="text-[13px] font-bold text-gray-900">
            Company: <span className="font-normal text-gray-700">{legalName || '—'}</span>
          </p>
          <SignatureField {...f} name="agreement.subscription.signature" />
          <Field {...f} name="agreement.subscription.printName" label="Print Name:" rules={{ required: REQ }} />
          <Field {...f} name="agreement.subscription.date" label="Date:" type="date" rules={{ required: REQ }} />
        </div>

        <div className={`space-y-4 border border-dashed ${BORDER} bg-[#faf9f7] p-4`}>
          <p className="text-[13px] font-bold text-gray-900">Company: <span className="font-normal text-gray-700">Everee, Inc.</span></p>
          {['Signature:', 'Print Name:', 'Date:'].map((l) => (
            <div key={l} className="flex items-end gap-3">
              <span className="text-[13px] font-semibold text-gray-500">{l}</span>
              <span className="mb-1 h-px flex-1 bg-gray-300" />
            </div>
          ))}
          <p className="text-xs italic text-gray-500">Countersigned by Everee once your agreement is accepted.</p>
        </div>
      </div>
    </div>
  );
};

export default StepCompanyInfo;
